/**
 * Infer a coarse activityClass from CRM page keys / paths for CEO time-costing.
 */
import type { CrmActivityClass, CrmActivityEntityType } from '../types';

const SALES_KEYS = new Set([
  'leads',
  'lead-new',
  'lead-edit',
  'clients',
  'pipeline',
  'proposal-builder',
  'proposals',
  'won-deals',
  'sales-activity',
  'sales-flow',
  'inspiration-board',
  'client-portal-mgmt',
  'client-portal',
  'utm-strategy',
  'marketing-hub',
]);

const MEETINGS_KEYS = new Set([
  'calendar',
  'executive-calendar',
  'messages',
  'notifications',
]);

const DESIGN_KEYS = new Set([
  'project-hub',
  'design-tracker',
  'kits',
  'dxf-nesting',
  'materials',
  'shop-schedule',
  'shop-management',
  'designer-portal',
  'architect-portal',
  'engineer-portal',
]);

const SITE_VISIT_KEYS = new Set([
  'people-travel',
  'map',
  'operations',
]);

const ADMIN_KEYS = new Set([
  'finances',
  'card-expenses',
  'billing-payables',
  'purchasing',
  'contractor-timesheets',
  'people',
  'people-handbook',
  'people-benefits',
  'people-hiring',
  'people-team',
  'people-travel',
  'people-achievements',
  'people-org',
  'executive',
  'reports',
  'analytics',
  'tools',
  'media',
  'team-invites',
  'projects',
  'vendors',
  'import',
  'agent-activity',
  'agent-harness',
  'journey',
  'dashboard',
  'home',
]);

export function inferCrmActivityClass(input: {
  pageKey: string;
  path?: string;
  entityType?: CrmActivityEntityType;
}): CrmActivityClass {
  // Job-attributed Project Hub context wins over the temporary page route.
  if (input.entityType === 'project') return 'design';

  const pageKey = (input.pageKey || '').trim();
  if (pageKey.startsWith('settings-')) return 'admin';
  // Finances sub-pages use pageKey finances-* (overview, project-pl, …).
  if (pageKey === 'finances' || pageKey.startsWith('finances-')) return 'admin';

  if (SALES_KEYS.has(pageKey)) return 'sales';
  if (MEETINGS_KEYS.has(pageKey)) return 'meetings';
  if (DESIGN_KEYS.has(pageKey)) return 'design';
  if (SITE_VISIT_KEYS.has(pageKey)) return 'site_visits';
  if (ADMIN_KEYS.has(pageKey)) return 'admin';

  if (input.entityType === 'lead' || input.entityType === 'deal' || input.entityType === 'proposal') {
    return 'sales';
  }

  return 'unknown';
}

export function crmActivityClassLabel(activityClass: CrmActivityClass): string {
  switch (activityClass) {
    case 'sales':
      return 'Sales';
    case 'meetings':
      return 'Meetings';
    case 'design':
      return 'Design';
    case 'admin':
      return 'Admin';
    case 'site_visits':
      return 'Site visits';
    default:
      return 'Unknown';
  }
}
